import * as React from 'react'
import { i18n as globalI18n, type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { useRouterState, type ErrorComponentProps } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { basename, cn } from './lib/utils'
import { withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { firstIncompleteTestPath, trpc } from './main'
import { classifyRouteError, type RouteErrorUser } from './route-error-classify'
import { logFrontendError } from './lib/log-error'

/**
 * Router-wide error boundary (wired as `defaultErrorComponent`).
 *
 * Components now read data with `useSuspenseQuery` (getUser, getReport, getMyEmployers, …). A suspense
 * query THROWS its error during render, so an error on a streamed query — e.g. an anonymous or
 * expired-session load of `/report` — would otherwise crash the whole tree (React #418) instead of
 * redirecting. We catch it per-route (so the server-rendered chrome stays) and decide what to do:
 *
 *   - No logged-in user (the getUser cache is null) → redirect to /login. This is the auth/expired
 *     case. We key off the user, NOT the error code, because a DEFERRED-streamed query's rejection has
 *     its message/code REDACTED by TanStack Start in production — so the thrown error often arrives as
 *     a bare "redacted" with no code, and only the absent session tells us it's an auth problem.
 *   - TEST_INCOMPLETE (message survives for non-streamed throws) → redirect to the first unfinished
 *     questionnaire (resume point), mirroring the client toastErrorLink in main.tsx.
 *   - Otherwise (a logged-in user hit a real error) → a graceful, non-crashing fallback.
 */
export function DefaultRouteError(props: ErrorComponentProps & { i18n?: I18n }) {
  // This is the router's `defaultErrorComponent`, so it's also the boundary for the ROOT route — and a
  // root-level error (e.g. a streamed getApp/getUser rejection thrown by AppConfigSync/AppUserSync)
  // renders it ABOVE AppShell's <I18nProvider>, where `useLingui` would throw "used without
  // I18nProvider" and blank the whole view (notably an SDK embed). Provide our own provider — the
  // `i18n` singleton is already loaded with the active catalog — so the boundary is self-sufficient.
  // Nesting under the app-level provider in the normal (child-route) case is harmless. Prefer the
  // request-scoped instance passed by the router (safe from concurrent SSR renders — React #418); fall
  // back to the shared global for callers that don't pass one. See createLocaleI18n / router.tsx.
  return (
    <I18nProvider i18n={props.i18n ?? globalI18n}>
      <RouteErrorContent {...props} />
    </I18nProvider>
  )
}

function RouteErrorContent({ error }: ErrorComponentProps) {
  const { t } = useLingui()
  const queryClient = useQueryClient()
  const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId ?? '' })
  // Duck-type rather than `instanceof TRPCClientError`: bundling can give the thrown error a different
  // class identity than the one imported here, so `instanceof` is unreliable across module boundaries.
  const err = error as { data?: { code?: string }; message?: string } | undefined
  const message = err?.message
  // No session → treat the error as auth (redirect to login) — but ONLY on an auth-required route, so
  // an error on a PUBLIC report (e.g. /market) never forces an anonymous viewer to log in. getUser is
  // authOptional and resolves to null when logged out, so an absent cached user means anonymous/expired.
  // (A streamed query's rejection is redacted in prod, so we key off the missing session, not the code.)
  const cachedUser = queryClient.getQueryData(trpc.auth.getUser.queryKey()) as RouteErrorUser | null
  // An applicant who hasn't finished the three questionnaires can't load a test-gated view
  // (report/teams/jobs): those reads reject with TEST_INCOMPLETE. The thrown message survives for a
  // NON-streamed throw, but a DEFERRED-streamed query's rejection is REDACTED in production — so the
  // error often arrives codeless. We infer the case from the CACHED user (a logged-in applicant with
  // the test still pending) on a test-gated route, instead of trusting the message. See classifyRouteError.
  const { isAuth, isTestIncomplete } = classifyRouteError({ routeId, cachedUser, message })

  // Report genuine crashes to the backend so they surface in the server log (CloudWatch/SST) — once
  // per boundary mount. Auth/test cases are normal redirect control-flow, not bugs, so skip them.
  const hasLogged = React.useRef(false)
  React.useEffect(() => {
    if (isAuth || isTestIncomplete || hasLogged.current) return
    hasLogged.current = true
    logFrontendError(error)
  }, [error, isAuth, isTestIncomplete])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth) {
      // resolveRedirectTarget unwraps the current URL so an auth error surfacing while already on /login
      // reuses the real destination instead of nesting /login into a fresh (re-encoded) ?redirect=.
      const target = resolveRedirectTarget(window.location.pathname + window.location.search)
      const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
      window.location.replace(withSdkParams(`${basename}/login${query}`))
    } else if (isTestIncomplete && !window.location.pathname.includes('/test')) {
      // Resume at the first unfinished questionnaire (competences → values → personality), mirroring
      // the client toastErrorLink in main.tsx — not a bare /test, which doesn't auto-forward. Carry the
      // API/SDK token-link session params so a token-link visitor isn't logged out by the hard redirect.
      window.location.replace(withSdkParams(`${basename}${firstIncompleteTestPath()}`))
    }
  }, [isAuth, isTestIncomplete])

  // Auth/test errors are mid-redirect — render nothing rather than flash an error.
  if (isAuth || isTestIncomplete) return null

  return (
    <div className="flex min-h-[40dvh] w-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-foreground text-base font-semibold">{t`Something went wrong`}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{t`We couldn’t load this page. Please try again.`}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className={cn('bg-primary text-primary-foreground mt-1 rounded-md px-4 py-2 text-sm font-medium')}
      >
        {t`Try again`}
      </button>
    </div>
  )
}
